// src/pages/public/Cart.jsx
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ShoppingCart, Trash2, Minus, Plus, ArrowRight, ArrowLeft,
  Loader2, Package, ShieldCheck, AlertCircle, X,
} from 'lucide-react'
import api from '../../lib/axios'
import { useCart } from '../../hooks/useCart'

const fmt = (n, currency = 'MAD') => {
  const symbol = currency === 'MAD' ? 'DH' : currency === 'EUR' ? '€' : currency
  return Number(n).toFixed(0) + ' ' + symbol
}

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())

function Field({ label, error, ...props }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#000a1e]">{label}</span>
      <input
        {...props}
        className={`mt-1.5 w-full px-4 py-3 rounded-xl bg-[#f9f9ff] border text-sm text-[#000a1e] transition-all focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] ${error ? 'border-red-400' : 'border-slate-200'}`}
      />
      {error && <span className="text-[11px] text-red-500 mt-1 block">{error}</span>}
    </label>
  )
}

function EmptyCart() {
  return (
    <div className="text-center py-20">
      <div className="mx-auto w-20 h-20 rounded-full bg-[#eff2ff] flex items-center justify-center mb-5">
        <ShoppingCart className="w-9 h-9 text-[#006b5f]/40" />
      </div>
      <h2 className="font-display font-bold text-2xl text-[#000a1e] mb-2">Votre panier est vide</h2>
      <p className="text-sm text-slate-500 mb-8">Découvrez nos supports NFC et commencez votre commande.</p>
      <Link to="/produits"
        className="inline-flex items-center gap-2 bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold px-6 py-3.5 rounded-xl shadow-lg shadow-[#006b5f]/20 transition-all hover:scale-105 active:scale-95">
        Voir les produits <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  )
}

export default function Cart() {
  const navigate = useNavigate()
  const { items, updateQty, removeItem, clear, count, total } = useCart()

  const [name, setName]   = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [notes, setNotes] = useState('')
  const [errors, setErrors] = useState({})
  const [sending, setSending] = useState(false)
  const [globalError, setGlobalError] = useState('')

  const submit = async (e) => {
    e.preventDefault()
    setGlobalError('')
    const err = {}
    if (name.trim().length < 2) err.name = 'Indiquez votre nom.'
    if (!isEmail(email)) err.email = 'Saisissez une adresse e-mail valide.'
    setErrors(err)
    if (Object.keys(err).length) return
    if (items.length === 0) return

    setSending(true)
    try {
      const { data } = await api.post('/public/cart/checkout', {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer_name:  name.trim(),
        customer_email: email.trim(),
        customer_phone: phone.trim() || null,
        notes:          notes.trim() || null,
      })
      const groupRef = data?.group_reference
      clear()
      navigate(`/panier/confirmation/${groupRef}`, {
        state: {
          groupRef,
          grandTotal: data?.grand_total,
          customerEmail: email.trim(),
          orders: data?.orders ?? [],
        },
      })
    } catch (e) {
      setGlobalError(e?.response?.data?.message || 'Impossible d\'enregistrer la commande.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#f9f9ff] pt-10 pb-20">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
        .font-display{font-family:'Plus Jakarta Sans',system-ui,sans-serif}
        .font-body{font-family:'Inter',system-ui,sans-serif}
      `}</style>

      <div className="max-w-6xl mx-auto px-6 font-body">
        <Link to="/produits" className="inline-flex items-center gap-2 text-sm text-[#006b5f] font-semibold hover:underline mb-6">
          <ArrowLeft className="w-4 h-4" /> Continuer mes achats
        </Link>

        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <h1 className="font-display font-bold text-4xl text-[#000a1e] tracking-tight flex items-center gap-3">
              <ShoppingCart className="w-9 h-9 text-[#006b5f]" />
              Mon panier
              {count > 0 && (
                <span className="text-sm font-semibold text-white bg-[#006b5f] px-3 py-1 rounded-full">
                  {count} article{count > 1 ? 's' : ''}
                </span>
              )}
            </h1>
            <p className="text-sm text-slate-500 mt-1.5">Vérifiez votre panier et finalisez votre commande.</p>
          </div>
          {items.length > 0 && (
            <button onClick={() => { if (window.confirm('Vider le panier ?')) clear() }}
              className="text-xs text-slate-500 hover:text-red-600 transition-colors flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> Vider le panier
            </button>
          )}
        </div>

        {items.length === 0 ? <EmptyCart /> : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              {items.map((item) => (
                <div key={item.product_id} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex gap-4">
                  {item.image ? (
                    <img src={item.image} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-[#eff2ff] flex items-center justify-center flex-shrink-0">
                      <Package className="w-8 h-8 text-[#006b5f]/40" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-display font-semibold text-[#000a1e] truncate">{item.name}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{fmt(item.price, item.currency)} / unité</p>
                      </div>
                      <button onClick={() => removeItem(item.product_id)}
                        className="text-slate-400 hover:text-red-600 transition-colors shrink-0" aria-label="Retirer">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 bg-[#eff2ff] rounded-xl p-1">
                        <button type="button" onClick={() => updateQty(item.product_id, item.quantity - 1)}
                          className="w-8 h-8 rounded-lg bg-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform">
                          <Minus className="w-3.5 h-3.5 text-[#000a1e]" />
                        </button>
                        <span className="w-8 text-center font-display font-bold text-sm text-[#000a1e]">{item.quantity}</span>
                        <button type="button" onClick={() => updateQty(item.product_id, item.quantity + 1)}
                          className="w-8 h-8 rounded-lg bg-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform">
                          <Plus className="w-3.5 h-3.5 text-[#000a1e]" />
                        </button>
                      </div>
                      <p className="font-display font-bold text-lg text-[#000a1e]">
                        {fmt(item.price * item.quantity, item.currency)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm sticky top-6">
                <h2 className="font-display font-bold text-xl text-[#000a1e] mb-5">Récapitulatif</h2>
                <div className="space-y-2 pb-5 mb-5 border-b border-slate-100">
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Sous-total</span>
                    <span className="font-semibold text-[#000a1e]">{fmt(total, items[0]?.currency)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Livraison</span>
                    <span className="font-semibold text-emerald-600">Offerte</span>
                  </div>
                </div>
                <div className="flex justify-between items-baseline mb-6">
                  <span className="text-sm font-semibold text-[#000a1e]">Total</span>
                  <span className="font-display font-extrabold text-2xl text-[#000a1e]">
                    {fmt(total, items[0]?.currency)}
                  </span>
                </div>
                <div className="space-y-3 mb-5">
                  <Field label="Nom complet" value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom" error={errors.name} />
                  <Field label="Adresse e-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.com" error={errors.email} />
                  <Field label="Téléphone (optionnel)" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="06 XX XX XX XX" />
                  <Field label="Notes (optionnel)" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Personnalisation…" />
                </div>
                {globalError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{globalError}</span>
                  </div>
                )}
                <button type="submit" disabled={sending}
                  className="w-full flex items-center justify-center gap-2 bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold py-3.5 rounded-xl shadow-lg shadow-[#006b5f]/20 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-60 disabled:hover:scale-100">
                  {sending ? (<><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours…</>) : (<>Passer la commande <ArrowRight className="w-4 h-4" /></>)}
                </button>
                <p className="mt-4 text-[11px] text-center text-slate-500 flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#006b5f]" />
                  Paiement à la livraison — nous vous contactons pour confirmer.
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}